
import { supabase } from '@/integrations/supabase/client';
import { RequestInterface } from '@/types/dashboard';

// Fetch user requests from the database - uses public view for anonymous users, full table for authenticated users
export const fetchUserRequests = async (): Promise<RequestInterface[]> => {
  // Check if user is authenticated
  const { data: { user } } = await supabase.auth.getUser();
  const isAuthenticated = !!user;

  let data, error;

  if (isAuthenticated) {
    // Use full table for authenticated users
    const result = await supabase
      .from('requests')
      .select('*')
      .eq('user_id', user.id) // 🔒 Defense in depth — explicit owner filter
      .order('created_at', { ascending: false });
    data = result.data;
    error = result.error;
  } else {
    // Use public view for anonymous users
    const result = await supabase
      .from('requests_public_view')
      .select('*')
      .order('created_at', { ascending: false });
    data = result.data;
    error = result.error;
  }

  if (error) {
    // Surface DB/RLS errors to caller — do NOT pretend the user has zero requests.
    console.error('Error fetching requests:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    console.log('No requests found in the database');
    return [];
  }

  return data.map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      description_original: item.description_original || null,
      original_language: item.original_language || null,
      date: new Date(item.date).toLocaleDateString('he-IL'),
      location: item.location,
      status: item.status,
      quotesCount: 0,
      timing: item.timing,
      media_urls: item.media_urls || [],
      category: item.category || '', // Keep compatibility
  }));
};


// Create a new request in the database
export const createRequest = async (requestData: {
  title: string;
  description: string;
  description_original?: string;
  original_language?: string;
  location: string;
  timing?: string;
  user_id?: string;
  category?: string;
  profession_id?: string | null;
  media_urls?: string[];
  latitude?: number | null;
  longitude?: number | null;
}): Promise<string | null> => {
  try {
    let userId = requestData.user_id;

    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('User not authenticated');
        return null;
      }
      userId = user.id;
    }

    // Save request with original language support
    const { data, error } = await supabase
      .from('requests')
      .insert({
        title: requestData.title,
        description: requestData.description,
        description_original: requestData.description_original || null,
        original_language: requestData.original_language || null,
        location: requestData.location,
        timing: requestData.timing || null,
        status: 'active',
        user_id: userId,
        category: requestData.category || requestData.title,
        profession_id: requestData.profession_id || null,
        media_urls: requestData.media_urls && requestData.media_urls.length > 0
          ? requestData.media_urls
          : null,
        latitude: requestData.latitude || null,
        longitude: requestData.longitude || null
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error creating request:', error);
      throw error;
    }

    return data && 'id' in data ? String(data.id) : null;
  } catch (error) {
    console.error('Error creating request:', error);
    return null;
  }
};

// Get a single request by ID
export const getRequestById = async (id: string): Promise<RequestInterface | null> => {
  try {
    // 🔒 Defense in depth — require authenticated user and filter by ownership
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('getRequestById: not authenticated');
      return null;
    }

    // We need to use "as any" to bypass TypeScript's type checking
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id) // 🔒
      .maybeSingle();
      
    if (error) {
      console.error('Error fetching request by ID:', error);
      return null;
    }
    
    if (!data) {
      console.log(`Request with ID ${id} not found in database`);
      return null;
    }
    
    // Use type assertion to ensure TypeScript knows the structure
    const request = data as any;
    
    // Return the request in the format expected by the interface
    return {
      id: request.id,
      title: request.title,
      description: request.description,
      description_original: request.description_original || null,
      original_language: request.original_language || null,
      date: new Date(request.date).toLocaleDateString('he-IL'),
      location: request.location,
      status: request.status,
      quotesCount: 0, // This will be updated when we fetch quotes
      timing: request.timing,
      media_urls: request.media_urls || [],
      category: request.category || ''
    };
  } catch (error) {
    console.error('Error fetching request by ID:', error);
    return null;
  }
};

// Update request status
export const updateRequestStatus = async (id: string, status: string): Promise<boolean> => {
  try {
    console.log(`Updating request ${id} status to ${status}`);
    // 🔒 Defense in depth — require authenticated user and filter by ownership
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('updateRequestStatus: not authenticated');
      return false;
    }

    // We need to use "as any" to bypass TypeScript's type checking
    const { error } = await supabase
      .from('requests')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id); // 🔒
      
    if (error) {
      console.error('Error updating request status:', error);
      return false;
    }
    
    console.log(`Request ${id} status updated to ${status} successfully`);
    return true;
  } catch (error) {
    console.error('Error updating request status:', error);
    return false;
  }
};

// Delete a request with improved error handling and verification
export const deleteRequest = async (id: string): Promise<boolean> => {
  try {
    console.log(`Starting deletion process for request with ID: ${id}`);

    // 🔒 Defense in depth — verify authenticated user owns the request before any cascading deletes
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('deleteRequest: not authenticated');
      return false;
    }

    const { data: ownership, error: ownershipError } = await supabase
      .from('requests')
      .select('id')
      .eq('id', id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (ownershipError) {
      console.error('deleteRequest: ownership check failed:', ownershipError);
      return false;
    }
    if (!ownership) {
      console.error(`deleteRequest: user ${user.id} does not own request ${id}`);
      return false;
    }

    // Step 1: Delete associated quotes from accepted_quotes table
    console.log(`Step 1: Deleting accepted quotes for request ${id}`);
    try {
      const { data: acceptedQuotesData, error: acceptedQuotesError } = await supabase
        .from('accepted_quotes')
        .select('quote_id')
        .eq('request_id', id);
        
      if (acceptedQuotesError) {
        console.error('Error finding associated accepted quotes:', acceptedQuotesError);
      } else {
        console.log(`Found ${acceptedQuotesData?.length || 0} accepted quotes to delete`);
        
        if (acceptedQuotesData && acceptedQuotesData.length > 0) {
          // Log each quote being deleted
          for (const record of acceptedQuotesData) {
            console.log(`Deleting accepted quote with quote_id: ${record.quote_id}`);
          }
          
          // Delete all accepted quotes for this request
          const { error: deleteAcceptedQuotesError } = await supabase
            .from('accepted_quotes')
            .delete()
            .eq('request_id', id);
            
          if (deleteAcceptedQuotesError) {
            console.error('Error deleting associated accepted quotes:', deleteAcceptedQuotesError);
          } else {
            console.log(`Successfully deleted accepted quotes for request ${id}`);
            
            // Verify deletion of accepted quotes
            const { data: verifyData, error: verifyError } = await supabase
              .from('accepted_quotes')
              .select('*')
              .eq('request_id', id);
              
            if (verifyError) {
              console.error('Error verifying accepted quotes deletion:', verifyError);
            } else if (verifyData && verifyData.length > 0) {
              console.error('Some accepted quotes were not deleted!', verifyData);
            } else {
              console.log('Verified: all accepted quotes were deleted successfully');
            }
          }
        }
      }
    } catch (e) {
      console.error('Error in deleting associated accepted quotes:', e);
      // Continue with the process despite errors
    }
    
    // Step 2: Get and delete all quotes for this request
    console.log(`Step 2: Finding and deleting quotes for request ${id}`);
    try {
      const { data: quotesData, error: quotesError } = await supabase
        .from('quotes')
        .select('id')
        .eq('request_id', id);
        
      if (quotesError) {
        console.error('Error finding associated quotes:', quotesError);
      } else {
        console.log(`Found ${quotesData?.length || 0} quotes to delete`);
        
        // Delete all quotes for this request
        if (quotesData && quotesData.length > 0) {
          // Log each quote being deleted
          for (const quote of quotesData) {
            console.log(`Deleting quote with ID: ${quote.id}`);
          }
          
          const { error: deleteQuotesError } = await supabase
            .from('quotes')
            .delete()
            .eq('request_id', id);
            
          if (deleteQuotesError) {
            console.error('Error deleting associated quotes:', deleteQuotesError);
          } else {
            console.log(`Successfully deleted quotes for request ${id}`);
            
            // Verify deletion of quotes
            const { data: verifyData, error: verifyError } = await supabase
              .from('quotes')
              .select('*')
              .eq('request_id', id);
              
            if (verifyError) {
              console.error('Error verifying quotes deletion:', verifyError);
            } else if (verifyData && verifyData.length > 0) {
              console.error('Some quotes were not deleted!', verifyData);
            } else {
              console.log('Verified: all quotes were deleted successfully');
            }
          }
        }
      }
    } catch (e) {
      console.error('Error in deleting associated quotes:', e);
      // Continue with the process despite errors
    }
    
    // Step 3: Finally, delete the request itself
    console.log(`Step 3: Deleting request ${id}`);
    const { error, data } = await supabase
      .from('requests')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id) // 🔒
      .select();
      
    if (error) {
      console.error('Error deleting request:', error);
      return false;
    }
    
    console.log(`Successfully deleted request ${id}:`, data);
    
    // Step 4: Verify the request has been deleted
    console.log(`Step 4: Verifying request ${id} was deleted`);
    const { data: verifyData, error: verifyError } = await supabase
      .from('requests')
      .select('*')
      .eq('id', id);
      
    if (verifyError) {
      console.error('Error verifying request deletion:', verifyError);
    } else {
      if (verifyData && verifyData.length > 0) {
        console.error('Request deletion verification failed - request still exists:', verifyData);
        return false;
      } else {
        console.log('Request deletion verified successfully - request no longer exists');
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting request:', error);
    return false;
  }
};
